<script setup>
import {ref, onMounted} from 'vue'
import Button from "@/common/Button.vue"
import api from '@/services/api'
import {usePageContentDataStore} from '@/stores/pageContentData'
import {useAppStore} from '@/stores/appStore'

const props = defineProps({
    options: {
        type: Object,
        required: true
    }
})

const emit = defineEmits(['hide-modal'])

const appStore = useAppStore()
const pageContentDataStore = usePageContentDataStore()

// --- State ---

const id = props.options.id
const contentData = ref([])
const loading = ref(true)

// --- Methods ---

const fetchData = async () => {
    loading.value = true
    try {
        const response = await api.get('api/component/content', {
            params: {contentType: id}
        })

        const items = response.data.items || []

        // Map items to their page instances
        contentData.value = items.flatMap(({id: contentId, title}) => {
            const pageInstances = pageContentDataStore.items.filter(o => o.contentId === contentId)

            if (pageInstances.length > 0) {
                return pageInstances.map(instance => ({
                    id: contentId,
                    pageContentId: instance.id,
                    title
                }))
            }

            // If the content exists but isn't placed on a page yet
            return [{id: contentId, title, pageContentId: null}]
        })
    } catch (error) {
        console.error("Failed to load usage data:", error)
    } finally {
        loading.value = false
    }
}

const goToSiteTree = (pageContentId) => {
    if (!pageContentId) return
    window.location.hash = `/site_tree/c${pageContentId}`
    emit('hide-modal')
}

const goToContent = (contentId) => {
    window.location.hash = `/content/${contentId}`
    emit('hide-modal')
}

const openWebsitePreview = (pageContentId) => {
    if (!pageContentId) return
    const token = localStorage.getItem('token')
    const host = appStore.ccmsSchemeAndHost
    const url = `${host}/controller/preview-open?pageContentId=${pageContentId}&token=${token}`

    window.open(url, '_blank')
}

// --- Lifecycle ---

onMounted(() => {
    fetchData()
})
</script>

<template>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">Usage Report</h4>
                <button
                    aria-label="Close"
                    class="btn-close"
                    type="button"
                    @click="$emit('hide-modal')"
                ></button>
            </div>

            <div class="modal-body p-0">
                <div v-if="loading" class="text-center p-4">
                    <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                    <span class="text-muted">Fetching usage data...</span>
                </div>

                <div v-else-if="contentData.length === 0" class="text-center p-4 text-muted">
                    This content type is not currently in use.
                </div>

                <ul v-else class="list-group list-group-flush">
                    <li
                        v-for="(content, index) in contentData"
                        :key="index"
                        class="list-group-item d-flex align-items-center py-2"
                    >
                        <div class="flex-fill text-truncate pe-2">
                            <span class="fw-bold">{{ content.title }}</span>
                        </div>

                        <div class="btn-group shadow-sm">
                            <Button
                                class="btn-sm btn-light"
                                title="Locate in Site Tree"
                                :class="{'disabled opacity-0': !content.pageContentId}"
                                @click="goToSiteTree(content.pageContentId)"
                            >
                                <i class="bi-diagram-3-fill"/>
                            </Button>

                            <Button
                                class="btn-sm btn-light"
                                title="Edit Content"
                                @click="goToContent(content.id)"
                            >
                                <i class="bi-pencil-square"/>
                            </Button>

                            <Button
                                class="btn-sm btn-light"
                                title="View on Website"
                                :class="{'disabled opacity-0': !content.pageContentId}"
                                @click="openWebsitePreview(content.pageContentId)"
                            >
                                <i class="bi-globe"/>
                            </Button>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<style scoped>
.btn-group .btn {
    border: 1px solid #dee2e6;
}
</style>